import React from 'react';
import SprintTreeItem from './SprintTreeItem';
import TemplateTreeItem from './TemplateTreeItem';
import { PlusIcon, ChevronIcon, CollapseAllIcon } from '../icons';
import { useCampaignsScrollThumb } from '../hooks/useCampaignsScrollThumb';

/**
 * CampaignsSection Component
 *
 * Workspace nav body:
 * - Campaigns tree fills leftover height and scrolls on its own
 * - Templates stay pinned in a capped bottom dock
 *   (Notion-style: expanding campaigns never hides those sections)
 *
 * Trash lives in the shared sidebar footer (campaigns + docs).
 */
const CampaignsSection = ({
  // Section state
  templatesExpanded,
  onToggleTemplates,
  
  // Campaigns
  topLevelSprints,
  nestedSprints,
  allSprints,
  selectedSprintId,
  expandedSprints,
  onSelectSprint,
  onRenameSprint,
  onSprintIconChange,
  onDeleteSprint,
  onNestCampaign,
  onReorderCampaign,
  onReorderNestedCampaign,
  onCreateNestedSprint,
  onToggleExpand,

  // Drag state
  draggedSprintId,
  onDragStart,
  onDragEnd,
  missionDropTargetSprintId,
  
  // Templates
  templates,
  nestedInTemplates,
  selectedTemplateId,
  templateCount,
  onSelectTemplate,
  onRenameTemplate,
  onTemplateIconChange,
  onDeleteTemplate,
  onDuplicateTemplate,
  onUseTemplate,
  onCreateTemplate,
  onCreateTemplateFromCampaign,
  
  // Open in new tab
  onOpenInNewTab,
  onCreateCampaign,
  onCollapseAllCampaigns,
  canCollapseAll = false,
}) => {
  const {
    scrollRef,
    trackRef,
    thumbRef,
    onPointerDown,
    onPointerMove,
    onPointerUp,
  } = useCampaignsScrollThumb();

  return (
    <div className="sidebar__workspace-body">
      <div className="sidebar__campaigns-pane">
      <div
        ref={scrollRef}
        className="sidebar__campaigns-scroll sidebar__pane-scroll sidebar__campaigns-scroll--native-hidden"
      >
        <div className="sidebar__campaigns">
            <div className="sidebar__campaign-tools">
              <button
                type="button"
                className="sidebar__campaign-new"
                onClick={onCreateCampaign}
                title="New campaign"
              >
                <PlusIcon size={10} />
                <span>New campaign</span>
              </button>
              <button
                type="button"
                className="sidebar__icon-btn sidebar__icon-btn--small"
                onClick={onCollapseAllCampaigns}
                disabled={!canCollapseAll}
                title="Collapse all nested campaigns and templates"
              >
                <CollapseAllIcon size={10} />
              </button>
            </div>
            {topLevelSprints.map((sprint, index) => (
              <SprintTreeItem
                key={sprint.id}
                sprint={sprint}
                nestedSprints={nestedSprints}
                allSprints={allSprints}
                selectedSprintId={selectedSprintId}
                onSelectSprint={onSelectSprint}
                onRenameSprint={onRenameSprint}
                onSprintIconChange={onSprintIconChange}
                onDeleteSprint={onDeleteSprint}
                onNestCampaign={onNestCampaign}
                onReorderCampaign={onReorderCampaign}
                onReorderNestedCampaign={onReorderNestedCampaign}
                onCreateNestedSprint={onCreateNestedSprint}
                onCreateTemplateFromCampaign={onCreateTemplateFromCampaign}
                onOpenInNewTab={onOpenInNewTab}
                depth={0}
                expandedSprints={expandedSprints}
                onToggleExpand={onToggleExpand}
                draggedSprintId={draggedSprintId}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                missionDropTargetSprintId={missionDropTargetSprintId}
                index={index}
              />
            ))}
          </div>
        </div>
        <div
          ref={trackRef}
          className="sidebar__scroll-thumb-track sidebar__scroll-thumb-track--hidden"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <div ref={thumbRef} className="sidebar__scroll-thumb" />
        </div>
      </div>

      <div className="sidebar__workspace-dock">
        <div className="sidebar__subsection">
          <div className="sidebar__subsection-header-row">
            <button
              className="sidebar__subsection-header"
              onClick={onToggleTemplates}
            >
              <span className="sidebar__subsection-title">Templates</span>
              <span className="sidebar__subsection-count">{templateCount}</span>
              <span className="sidebar__subsection-chevron">
                <ChevronIcon size={9} expanded={templatesExpanded} />
              </span>
            </button>
            <button
              className="sidebar__icon-btn sidebar__icon-btn--small sidebar__icon-btn--accent"
              onClick={onCreateTemplate}
              title="New template"
            >
              <PlusIcon size={10} />
            </button>
          </div>

          {templatesExpanded && (
            <div className="sidebar__subsection-content sidebar__pane-scroll">
              {templates.length > 0 ? (
                templates.map(template => (
                  <TemplateTreeItem
                    key={template.id}
                    template={template}
                    nestedInTemplates={nestedInTemplates}
                    selectedTemplateId={selectedTemplateId}
                    selectedSprintId={selectedSprintId}
                    expandedSprints={expandedSprints}
                    onToggleExpand={onToggleExpand}
                    onSelectTemplate={onSelectTemplate}
                    onSelectSprint={onSelectSprint}
                    onRenameTemplate={onRenameTemplate}
                    onTemplateIconChange={onTemplateIconChange}
                    onDeleteTemplate={onDeleteTemplate}
                    onDuplicateTemplate={onDuplicateTemplate}
                    onUseTemplate={onUseTemplate}
                    onCreateNestedSprint={onCreateNestedSprint}
                    onRenameSprint={onRenameSprint}
                    onSprintIconChange={onSprintIconChange}
                    onDeleteSprint={onDeleteSprint}
                    onOpenInNewTab={onOpenInNewTab}
                  />
                ))
              ) : (
                <div className="sidebar__empty sidebar__empty--small">No templates</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignsSection;
